/* Historique — Socle du moteur prédictif Noam.
 *
 * Chaque snapshot est un instantané complet du patrimoine décisionnel
 * (gravités, fractales, scores, signaux, plans, fantômes, marchés…).
 * En comparant deux snapshots, Noam peut détecter les cassures de tendance
 * et alimenter les futurs modules de simulation prédictive.
 *
 * Cette itération : liste, capture manuelle, delta entre deux snapshots.
 * À terme : projection, scénarios, boussole.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { Loader2, Camera, TrendingUp, TrendingDown, Minus, ArrowRight, Clock, Sparkles, Zap } from "lucide-react";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("fr-CH", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

// Labels affichables pour les clés flatten « metrics.* »
const LABEL_MAP = {
  "counts.signaux_total": "Signaux radar",
  "counts.risques_total": "Risques",
  "counts.opportunites_total": "Opportunités",
  "counts.entites_total": "Entités annuaire",
  "counts.documents_total": "Documents",
  "counts.plans_total": "Plans d'action",
  "counts.incidents_total": "Incidents",
  "counts.alertes_total": "Alertes",
  "counts.fournisseurs_total": "Fournisseurs",
  "counts.audits_total": "Audits",
  "counts.controles_total": "Contrôles",
  "counts.negociations_total": "Négociations",
  "counts.analyses_total": "Analyses",
  "counts.chaines_total": "Chaînes",
  "counts.chain_nodes_total": "Nœuds de chaîne",
  "counts.chain_links_total": "Liens de chaîne",
  "counts.frictions_total": "Frictions",
  "counts.fantomes_total": "Fantômes détectés",
  "counts.trends_total": "Trends surveillés",
  "counts.financements_total": "Financements",
  "counts.capital_sources_total": "Sources de capital",
  "counts.hr_postes_total": "Postes RH",
  "counts.cartos_total": "Cartographies",
  "counts.courtage_mandates": "Mandats courtage",
  "counts.courtage_assets": "Actifs courtage",
  "counts.brvm_screens_total": "Scans screener",
  "counts.bras_arme_total": "Analyses bras armé",
  "counts.meta_runs_total": "Runs métacognition",
  "counts.doctrines_total": "Doctrines actives",
  "counts.briefs_total": "Briefs publiés",
  "signaux.unread": "Signaux non lus",
  "frictions.cost_meur_monthly": "Coût frictions (M€/mois)",
  "frictions.avg_score": "Score friction moyen",
  "fantomes.cost_meur_monthly": "Coût inaction fantômes (M€/mois)",
  "fantomes.critical": "Fantômes critiques",
  "fantomes.major": "Fantômes majeurs",
  "plans.open": "Plans ouverts",
  "plans.done": "Plans terminés",
  "plans.late": "Plans en retard",
};

function DeltaBadge({ pct, abs }) {
  const pos = pct > 0;
  const neg = pct < 0;
  const cls = pos
    ? "text-[#1D9E75] bg-[#1D9E75]/10 border-[#1D9E75]/30"
    : neg
      ? "text-[#E24B4A] bg-[#E24B4A]/10 border-[#E24B4A]/30"
      : "text-zinc-400 bg-white/[0.04] border-white/10";
  const Icon = pos ? TrendingUp : neg ? TrendingDown : Minus;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${cls}`}>
      <Icon className="w-3 h-3" />
      {pos ? "+" : ""}{Number.isFinite(pct) ? pct : 0}%
      {abs !== undefined && Math.abs(abs) > 0 && (
        <span className="text-[9px] opacity-70 ml-0.5">({abs > 0 ? "+" : ""}{abs})</span>
      )}
    </span>
  );
}

export default function Historique() {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [capturing, setCapturing] = useState(false);
  const [selA, setSelA] = useState(null);
  const [selB, setSelB] = useState(null);
  const [diff, setDiff] = useState(null);
  const [loadingDiff, setLoadingDiff] = useState(false);
  const [breakouts, setBreakouts] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, rb] = await Promise.all([
        api.get("/workspace/snapshots?limit=100"),
        api.get("/workspace/snapshots/breakouts").catch(() => ({ data: null })),
      ]);
      const items = r.data?.items || [];
      setSnapshots(items);
      setBreakouts(rb.data || null);
      if (items.length >= 2) {
        setSelA(items[items.length - 1].id);
        setSelB(items[0].id);
      } else if (items.length === 1) {
        setSelB(items[0].id);
      }
    } catch (e) {
      notify.error(fmtError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const capture = async () => {
    setCapturing(true);
    try {
      await api.post("/workspace/snapshots/capture");
      notify.success("Snapshot capturé — un nouveau point d'ancrage dans votre trajectoire.");
      await load();
    } catch (e) {
      notify.error(fmtError(e));
    } finally {
      setCapturing(false);
    }
  };

  const computeDiff = async () => {
    if (!selA || !selB || selA === selB) return;
    setLoadingDiff(true);
    try {
      const r = await api.get(`/workspace/snapshots/compare?from=${selA}&to=${selB}`);
      setDiff(r.data);
    } catch (e) {
      notify.error(fmtError(e));
    } finally {
      setLoadingDiff(false);
    }
  };

  const sortedDeltas = useMemo(() => {
    if (!diff?.deltas) return [];
    return [...diff.deltas]
      .filter((d) => d.delta_abs !== 0 || d.a !== 0 || d.b !== 0)
      .sort((a, b) => Math.abs(b.delta_abs) - Math.abs(a.delta_abs));
  }, [diff]);

  const movers = useMemo(() => sortedDeltas.filter((d) => d.delta_abs !== 0).slice(0, 5), [sortedDeltas]);

  return (
    <>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#EF9F27] font-semibold">Historique · Boussole prédictive</div>
          <h1 className="noam-display text-2xl mt-1">L'axe du temps de votre trajectoire.</h1>
          <p className="text-xs text-zinc-400 mt-1 max-w-2xl">
            Chaque semaine, Noam capture un instantané complet de votre patrimoine décisionnel — gravités, fractales, scores, signaux, plans, marchés. Ces points d&apos;ancrage temporels sont le socle sur lequel Noam construira sa capacité à <span className="text-white font-semibold">projeter</span>, <span className="text-white font-semibold">simuler</span> et <span className="text-white font-semibold">anticiper</span>. Prendre un snapshot manuel avant un moment clé (comité, closing, arbitrage) crée un « avant » propre pour évaluer l&apos;impact ensuite.
          </p>
        </div>
        <button
          onClick={capture}
          disabled={capturing}
          className="text-[11px] px-3 py-1.5 rounded bg-[#EF9F27] text-black font-semibold hover:bg-[#EF9F27]/90 inline-flex items-center gap-1.5 disabled:opacity-50 transition-colors flex-shrink-0"
          data-testid="snapshot-capture-btn"
          title="Capturer maintenant l'état complet de votre workspace"
        >
          {capturing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
          Capturer maintenant
        </button>
      </div>

      {loading ? (
        <div className="text-[11px] text-zinc-500 flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" /> Chargement de l&apos;historique…
        </div>
      ) : snapshots.length === 0 ? (
        <div className="noam-card p-10 text-center space-y-3" data-testid="snapshot-empty">
          <Clock className="w-8 h-8 mx-auto text-zinc-600" />
          <div className="text-sm text-white font-semibold">Aucun snapshot pour l&apos;instant.</div>
          <p className="text-[11px] text-zinc-400 max-w-md mx-auto">
            Cliquez sur <span className="text-[#EF9F27] font-semibold">« Capturer maintenant »</span> pour créer votre premier point d&apos;ancrage. À partir de cette semaine, Noam en capturera un automatiquement chaque semaine.
          </p>
        </div>
      ) : (
        <>
          {/* Comparateur */}
          <div className="noam-card p-4 space-y-3" data-testid="snapshot-compare">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
              <Sparkles className="w-3 h-3 text-[#EF9F27]" />
              Comparer deux instantanés
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={selA || ""}
                onChange={(e) => setSelA(e.target.value)}
                className="text-[11px] bg-white/[0.03] border border-white/15 rounded px-2 py-1.5 text-white focus:outline-none focus:border-white/30 min-w-0 flex-1"
                data-testid="snapshot-select-a"
              >
                <option value="">-- Avant --</option>
                {snapshots.map((s) => (
                  <option key={s.id} value={s.id}>{fmtDate(s.captured_at)} · {s.iso_week} {s.auto ? "" : "·M"}</option>
                ))}
              </select>
              <ArrowRight className="w-4 h-4 text-zinc-500 flex-shrink-0" />
              <select
                value={selB || ""}
                onChange={(e) => setSelB(e.target.value)}
                className="text-[11px] bg-white/[0.03] border border-[#EF9F27]/40 rounded px-2 py-1.5 text-white focus:outline-none focus:border-[#EF9F27]/80 min-w-0 flex-1"
                data-testid="snapshot-select-b"
              >
                <option value="">-- Après --</option>
                {snapshots.map((s) => (
                  <option key={s.id} value={s.id}>{fmtDate(s.captured_at)} · {s.iso_week} {s.auto ? "" : "·M"}</option>
                ))}
              </select>
              <button
                onClick={computeDiff}
                disabled={!selA || !selB || selA === selB || loadingDiff}
                className="text-[11px] px-3 py-1.5 rounded border border-[#EF9F27]/60 text-[#EF9F27] hover:bg-[#EF9F27]/10 inline-flex items-center gap-1 disabled:opacity-40"
                data-testid="snapshot-diff-btn"
              >
                {loadingDiff ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                Analyser le delta
              </button>
            </div>

            {diff && (
              <div className="pt-3 border-t border-white/5 space-y-3" data-testid="snapshot-diff-result">
                {movers.length > 0 && (
                  <div className="text-[11px] text-zinc-300">
                    <span className="text-[#EF9F27] font-semibold">Ce qui a le plus bougé :</span>{" "}
                    {movers.map((m, i) => (
                      <span key={m.key}>
                        <span className="text-white font-semibold">{LABEL_MAP[m.key] || m.key}</span>
                        <span className={m.delta_abs > 0 ? "text-[#1D9E75]" : "text-[#E24B4A]"}>
                          {" "}({m.delta_abs > 0 ? "+" : ""}{m.delta_abs})
                        </span>
                        {i < movers.length - 1 ? ", " : "."}
                      </span>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {sortedDeltas.map((d) => (
                    <div key={d.key} className="border border-white/10 rounded p-2.5 bg-white/[0.02] flex items-center justify-between gap-3 min-w-0" data-testid={`snapshot-delta-${d.key.replace(/\./g, '-')}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] text-zinc-500 truncate uppercase tracking-wide">{LABEL_MAP[d.key] || d.key}</div>
                        <div className="text-sm text-white font-semibold tabular-nums">
                          {d.a} <span className="text-zinc-600 text-[10px] mx-1">→</span> {d.b}
                        </div>
                      </div>
                      <DeltaBadge pct={d.delta_pct} abs={d.delta_abs} />
                    </div>
                  ))}
                  {sortedDeltas.length === 0 && (
                    <div className="text-[11px] text-zinc-500 col-span-full">Aucun mouvement entre ces deux snapshots.</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Cassures de tendance (V23.15.57 — Brique 2) */}
          {breakouts && breakouts.breakouts && breakouts.breakouts.length > 0 && (
            <div className="noam-card p-4 border border-[#EF9F27]/30" data-testid="snapshot-breakouts">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-semibold text-[#EF9F27] mb-3">
                <Zap className="w-3 h-3" />
                Cassures détectées · dernier snapshot vs {breakouts.baseline_window} précédents
              </div>
              <p className="text-[11px] text-zinc-400 mb-3">
                Ces indicateurs ont bougé de manière anormale par rapport à leur régime habituel. Un signal précoce vaut mieux qu'un tableau de bord parfait.
              </p>
              <ul className="space-y-1.5">
                {breakouts.breakouts.slice(0, 8).map((b) => {
                  const label = LABEL_MAP[b.key] || b.key;
                  const isUp = b.direction === "up";
                  const color = isUp ? "text-[#1D9E75]" : "text-[#E24B4A]";
                  const Icon = isUp ? TrendingUp : TrendingDown;
                  return (
                    <li key={b.key} className="flex items-center gap-3 py-1.5 border-b border-white/5 last:border-0" data-testid={`snapshot-breakout-${b.key.replace(/\./g, '-')}`}>
                      <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${color}`} />
                      <div className="min-w-0 flex-1">
                        <div className="text-[12px] text-white font-semibold truncate">{label}</div>
                        <div className="text-[10px] text-zinc-500 truncate">
                          moyenne {b.baseline_mean.toFixed(1)} → maintenant <span className="text-white">{b.current.toFixed(0)}</span> · trigger {b.trigger === "sigma" ? `σ (${b.z_score.toFixed(1)})` : `${Math.abs(b.delta_pct).toFixed(0)}%`}
                        </div>
                      </div>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border flex-shrink-0 ${color} border-current/30 bg-current/5`}>
                        {isUp ? "+" : ""}{b.delta_pct}%
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Timeline */}
          <div className="noam-card p-4 space-y-2" data-testid="snapshot-timeline">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-2">
              <Clock className="w-3 h-3 text-[#378ADD]" />
              Timeline · {snapshots.length} point{snapshots.length > 1 ? "s" : ""} d&apos;ancrage
            </div>
            <ul className="space-y-1">
              {snapshots.map((s) => (
                <li key={s.id} className="flex items-center gap-3 py-1.5 border-b border-white/5 last:border-0 min-w-0" data-testid={`snapshot-item-${s.id}`}>
                  <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${s.auto ? "bg-[#378ADD]" : "bg-[#EF9F27]"}`} title={s.auto ? "Automatique (hebdo)" : "Manuel"} />
                  <div className="text-[11px] text-white flex-shrink-0 tabular-nums">{fmtDate(s.captured_at)}</div>
                  <div className="text-[10px] text-zinc-500 flex-shrink-0">{s.iso_week}</div>
                  <div className="text-[10px] text-zinc-400 truncate flex-1">
                    {s.metrics?.counts?.signaux_total ?? 0} signaux · {s.metrics?.counts?.entites_total ?? 0} entités · {s.metrics?.counts?.plans_total ?? 0} plans
                  </div>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded flex-shrink-0 ${s.auto ? "bg-[#378ADD]/10 text-[#378ADD]" : "bg-[#EF9F27]/10 text-[#EF9F27]"}`}>
                    {s.auto ? "auto" : "manuel"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </>
  );
}
